import { useMemo, useState } from "react";
import {
  AlertTriangle,
  BadgeCheck,
  CheckCircle2,
  Circle,
  ClipboardCheck,
  Loader2,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import { checkIdentity } from "@/lib/identityApi";
import { formatApiError } from "@/lib/api";
import {
  mapIdentityApiToCheckIdResult,
  resolveCheckIdSourceModule,
} from "@/lib/identityCheckMapper";
import { CHECK_ID_STATUS, statusMeta } from "@/lib/checkIdLocalValidation";
import { toast } from "sonner";

const VARIANT_COPY = {
  laboral: {
    title: "Check ID del trabajador",
    subtitle:
      "Valide RFC, CURP, NSS y consistencia antes del alta o inscripción IMSS. Validación centralizada Check-ID (estructural + fuentes locales cuando existan).",
    personLabel: "Nombre del trabajador",
    nssLabel: "NSS / Número IMSS",
    expedienteLabel: "Estado del expediente laboral",
    validateCta: "Validar datos del trabajador",
    offlineNote:
      "Las consultas SAT, IMSS y RENAPO en vivo aparecen como «pendiente integración» hasta conectar los proveedores correspondientes.",
  },
  tramites: {
    title: "Check ID fiscal",
    subtitle:
      "Valide RFC, CURP, NSS y datos fiscales básicos del contribuyente o persona física. Validación centralizada Check-ID.",
    personLabel: "Nombre de la persona / contribuyente",
    nssLabel: "NSS (si aplica)",
    expedienteLabel: "Estado de datos fiscales",
    validateCta: "Validar datos fiscales",
    offlineNote:
      "Revise formatos y coherencia antes de ejecutar trámites fiscales. Consultas externas pendientes de integración se indican en la lista de verificación.",
  },
  "tramites-nss": {
    title: "Validador NSS (IMSS / IDSE)",
    subtitle:
      "Prepare y valide NSS, RFC, CURP y consistencia antes de trámites IMSS/IDSE. Validación centralizada Check-ID.",
    personLabel: "Nombre del trabajador / asegurado",
    nssLabel: "NSS / Número IMSS",
    expedienteLabel: "Estado de validación NSS",
    validateCta: "Validar NSS y datos",
    offlineNote:
      "No se envían datos a IDSE ni al portal IMSS en vivo. Use esta pantalla para revisar formatos antes del trámite.",
  },
};

function CheckIcon({ ok, pendingIntegration }) {
  if (pendingIntegration) {
    return <Circle className="w-4 h-4 text-sky-500 shrink-0" aria-hidden />;
  }
  if (ok === true) return <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" aria-hidden />;
  if (ok === false) return <XCircle className="w-4 h-4 text-rose-600 shrink-0" aria-hidden />;
  return <Circle className="w-4 h-4 text-slate-400 shrink-0" aria-hidden />;
}

const EMPTY_FORM = {
  nombre: "",
  rfc: "",
  curp: "",
  nss: "",
  fechaNacimiento: "",
  codigoPostal: "",
  regimenFiscal: "",
  observaciones: "",
};

/**
 * Panel reutilizable Check ID — validación vía POST /api/identity/check + campos locales opcionales.
 * @param {"laboral"|"tramites"|"tramites-nss"} variant
 */
export default function CheckIdValidator({ variant = "laboral", className = "", clientId = "" }) {
  const copy = VARIANT_COPY[variant] || VARIANT_COPY.laboral;
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const meta = useMemo(() => (result ? statusMeta(result.status) : null), [result]);

  const setField = (key) => (e) => {
    const value = e?.target?.value ?? "";
    setForm((prev) => ({ ...prev, [key]: value }));
    setResult(null);
  };

  const handleValidate = async () => {
    const rfcN = (form.rfc || "").trim();
    const curpN = (form.curp || "").trim();
    const nssN = (form.nss || "").trim();
    if (!rfcN && !curpN && !nssN) {
      toast.error("Indique al menos RFC, CURP o NSS para validar");
      return;
    }
    setLoading(true);
    try {
      const apiResponse = await checkIdentity({
        rfc: rfcN,
        curp: curpN,
        nss: nssN,
        client_id: clientId,
        source_module: resolveCheckIdSourceModule(variant),
      });
      setResult(mapIdentityApiToCheckIdResult(apiResponse, form));
    } catch (err) {
      setResult(null);
      toast.error(formatApiError(err, "No se pudo ejecutar la validación Check-ID"));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setForm(EMPTY_FORM);
    setResult(null);
  };

  return (
    <div className={`space-y-6 ${className}`} data-testid={`check-id-validator-${variant}`}>
      <div className="rounded-lg border border-[#0F2D5C]/15 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-[#0F2D5C]/10 bg-[#0F2D5C] px-5 py-4 text-white">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-sm bg-white/10 shrink-0">
              <ShieldCheck className="w-5 h-5" strokeWidth={1.75} aria-hidden />
            </div>
            <div>
              <h2 className="text-base font-semibold m-0">{copy.title}</h2>
              <p className="text-xs text-white/75 mt-1 m-0 leading-relaxed max-w-2xl">{copy.subtitle}</p>
            </div>
          </div>
        </div>

        <div className="p-5 sm:p-6 grid gap-6 lg:grid-cols-[1fr_320px]">
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor={`check-id-nombre-${variant}`}>{copy.personLabel}</Label>
                <Input
                  id={`check-id-nombre-${variant}`}
                  value={form.nombre}
                  onChange={setField("nombre")}
                  placeholder="Ej. Juan Pérez García"
                  data-testid="check-id-nombre"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`check-id-rfc-${variant}`}>RFC</Label>
                <Input
                  id={`check-id-rfc-${variant}`}
                  value={form.rfc}
                  onChange={setField("rfc")}
                  placeholder="XXXX000000XXX"
                  className="font-mono uppercase"
                  data-testid="check-id-rfc"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`check-id-curp-${variant}`}>CURP</Label>
                <Input
                  id={`check-id-curp-${variant}`}
                  value={form.curp}
                  onChange={setField("curp")}
                  placeholder="XXXX000000HXXXXX00"
                  className="font-mono uppercase"
                  data-testid="check-id-curp"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`check-id-nss-${variant}`}>{copy.nssLabel}</Label>
                <Input
                  id={`check-id-nss-${variant}`}
                  value={form.nss}
                  onChange={setField("nss")}
                  placeholder="11 dígitos"
                  className="font-mono"
                  inputMode="numeric"
                  data-testid="check-id-nss"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`check-id-fecha-${variant}`}>Fecha de nacimiento</Label>
                <Input
                  id={`check-id-fecha-${variant}`}
                  type="date"
                  value={form.fechaNacimiento}
                  onChange={setField("fechaNacimiento")}
                  data-testid="check-id-fecha"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`check-id-cp-${variant}`}>Código postal fiscal</Label>
                <Input
                  id={`check-id-cp-${variant}`}
                  value={form.codigoPostal}
                  onChange={setField("codigoPostal")}
                  placeholder="00000"
                  className="font-mono"
                  inputMode="numeric"
                  data-testid="check-id-cp"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`check-id-regimen-${variant}`}>Régimen fiscal</Label>
                <Input
                  id={`check-id-regimen-${variant}`}
                  value={form.regimenFiscal}
                  onChange={setField("regimenFiscal")}
                  placeholder="Ej. 605 — Sueldos y salarios"
                  data-testid="check-id-regimen"
                />
              </div>
              <div className="sm:col-span-2 space-y-1.5">
                <SpellcheckTextarea
                  id={`check-id-obs-${variant}`}
                  label="Observaciones"
                  value={form.observaciones}
                  onChange={setField("observaciones")}
                  rows={2}
                  placeholder="Notas internas del expediente o del trámite"
                  testId="check-id-observaciones"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                type="button"
                className="bg-[#3DA873] hover:bg-[#349966] text-white"
                onClick={handleValidate}
                disabled={loading}
                data-testid="check-id-validar"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-2" />}
                {copy.validateCta}
              </Button>
              <Button type="button" variant="outline" onClick={handleReset} disabled={loading}>
                Limpiar
              </Button>
            </div>

            <p className="text-[11px] text-slate-500 m-0 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600" aria-hidden />
              {copy.offlineNote}
            </p>
          </div>

          <aside className="space-y-4">
            <div
              className={`rounded-lg border p-4 ${result ? meta.tone : "bg-slate-50 border-slate-200 text-slate-600"}`}
              data-testid="check-id-status-panel"
            >
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-70 m-0">
                {copy.expedienteLabel}
              </p>
              {result ? (
                <>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} aria-hidden />
                    <span className="text-sm font-bold">{meta.label}</span>
                  </div>
                  <p className="text-xs mt-2 m-0 leading-relaxed">{result.summary}</p>
                </>
              ) : (
                <p className="text-xs mt-2 m-0">Capture los datos y pulse «Validar» para ver el semáforo.</p>
              )}
            </div>

            {result ? (
              <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2" data-testid="check-id-checklist">
                <p className="text-xs font-bold text-[#0F2D5C] m-0 flex items-center gap-1.5">
                  <BadgeCheck className="w-4 h-4" aria-hidden />
                  Checklist de validación
                </p>
                <ul className="space-y-2 m-0 p-0 list-none">
                  {result.checks.map((item) => (
                    <li key={item.id} className="flex items-start gap-2 text-xs text-slate-700">
                      <CheckIcon ok={item.ok} pendingIntegration={item.pendingIntegration} />
                      <span>
                        <span className="font-semibold text-slate-800">{item.label}:</span>{" "}
                        {item.message}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {result?.warnings?.length ? (
              <div
                className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                data-testid="check-id-warnings"
              >
                <p className="font-semibold m-0 mb-1">Avisos del servicio</p>
                <ul className="m-0 pl-4 space-y-0.5">
                  {result.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {result?.status === CHECK_ID_STATUS.INCONSISTENT ? (
              <div
                className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900"
                data-testid="check-id-alertas"
              >
                <p className="font-semibold m-0 mb-1">Alertas de inconsistencia</p>
                <ul className="m-0 pl-4 space-y-0.5">
                  {result.checks
                    .filter((c) => c.ok === false)
                    .map((c) => (
                      <li key={c.id}>{c.label}: {c.message}</li>
                    ))}
                </ul>
              </div>
            ) : null}
          </aside>
        </div>
      </div>
    </div>
  );
}
